// eslint-disable-next-line no-restricted-imports
import { useEffect } from 'react';
import { useToasts } from '@applyboard/crystal-ui';
import { useEventTracker, useFeatureDecision } from '@applyboard/ui-utils';
import DuolingoBanner from '../../../assets/DuolingoBanner.png';
import GreBanner from '../../../assets/GreBanner.png';
import PteBanner from '../../../assets/PteBanner.png';
import ToeflBanner from '../../../assets/ToeflBanner.png';
import { appendQueryParameters } from '../../../common/utils/appendQueryParameters';
import { getApplyShopConfig, useGetProducts } from '../../../data/applyshopStore/v3/applyShopStore';
import { ApplyShopStoreProductWithVariants } from '../../../data/applyshopStore/v3/types';
import { Country, useCountries } from '../../../data/countries/useCountries';
import { convertIsoToCountryName } from '../../../utils/convertIsoToCountryName';
import {
  getBestAvailablePrices,
  getTestCardPricing,
  isProductSoldOut,
} from '../../../utils/formatPrice';

import { applyCardFilters } from '../../VasHub/ServiceListings/filterUtils';

import { VAS_HUB_TRACKING_SOURCE } from '../../VasHub/constants';

export type StudentLanguageTestCardsProps = {
  userId: string;
  agencyCountry?: string; // This is not treuly the agency country, it is the country of the student
  filters?: {
    nationality?: string;
    destination?: string[];
  };
};

const selectBestVariant = (
  product: ApplyShopStoreProductWithVariants | undefined,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
) => {
  if (!product || !product.variants || product.variants.length === 0) return undefined;
  const variants = product.variants;
  // Convert ISO codes to country names for matching against Shopify variants
  const preferredCountryNames = preferredIsoCodes.map(isoCode =>
    convertIsoToCountryName(isoCode, countries),
  );
  const prefs = new Set(preferredCountryNames.map(c => c.toLowerCase()));

  // 1) Prefer available variant that matches preferred nationality
  const preferredAvailable =
    variants.find(v => prefs.size > 0 && prefs.has(v.country.toLowerCase()) && v.isAvailable) ||
    null;
  if (preferredAvailable) return preferredAvailable;

  // 2) Else any variant that matches preferred nationality (even if not available)
  const preferredAny =
    variants.find(v => prefs.size > 0 && prefs.has(v.country.toLowerCase())) || null;
  if (preferredAny) return preferredAny;

  // 3) No fallback - return null if no preferred variants
  return null;
};

const getApplyButtonLink = (
  product: ApplyShopStoreProductWithVariants | undefined,
  userId: string,
  agencyCountry: string,
  isNepalShopifyStoreEnabled: boolean,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
): string => {
  if (!product) return '';

  // For India/Nepal stores: use first variant directly (single variant per product)
  // For ROW store: use nationality-based variant selection (multi-variant products)
  const variant =
    agencyCountry === 'IN' || (agencyCountry === 'NP' && isNepalShopifyStoreEnabled)
      ? product.variants?.[0]
      : selectBestVariant(product, preferredIsoCodes, countries);

  if (variant?.applyShopCartLink) {
    return appendQueryParameters(variant.applyShopCartLink, {
      source: VAS_HUB_TRACKING_SOURCE,
    });
  }

  // Fallback to product page instead of empty string
  return appendQueryParameters(product.onlineStoreUrl ?? '', {
    source: VAS_HUB_TRACKING_SOURCE,
  });
};

const getVendorUrl = (product: ApplyShopStoreProductWithVariants | undefined): string => {
  if (!product) {
    return '';
  }

  return appendQueryParameters(product.onlineStoreUrl ?? '', {
    source: VAS_HUB_TRACKING_SOURCE,
  });
};

export const useStudentLanguageTestCardData = ({
  userId,
  agencyCountry = '',
  filters = {},
}: StudentLanguageTestCardsProps) => {
  const toast = useToasts();
  const { trackEvent } = useEventTracker();
  const [decisionEngine, decisionEngineReady] = useFeatureDecision();
  const isNepalShopifyStoreEnabled =
    decisionEngine?.isFeatureEnabled('vas_nepal_shoppify_store') ?? false;
  const { countries } = useCountries();

  const { products, productsError, isProductsLoading } = useGetProducts(
    { applyShopConfig: getApplyShopConfig(agencyCountry, isNepalShopifyStoreEnabled) },
    { enabled: true }, // Students can browse test cards without being logged in
  );

  const duolingoProduct = products?.find(
    product =>
      product.handle.toLowerCase().includes('det') ||
      product.handle.toLowerCase().includes('duolingo'),
  );
  const pteProduct = products?.find(product => product.handle.toLowerCase().includes('pte'));
  const toeflProduct = products?.find(product => product.handle.toLowerCase().includes('toefl'));
  const greProduct = products?.find(product => product.handle.toLowerCase().includes('gre'));

  useEffect(() => {
    if (productsError) {
      trackEvent({
        event: 'VAS - Vas Hub Page - Failed to fetch products for ServiceListings',
        data: { user_id: userId || 'anonymous' },
        error: productsError,
      });

      toast.negative(new Error('Unable to fetch products, some services may not be available'));
    }
  }, [productsError, toast, trackEvent, userId]);

  const cardsData = [
    (() => {
      const priceData = getBestAvailablePrices(
        pteProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      const pricing = priceData
        ? getTestCardPricing(
            priceData.discountedAmount,
            priceData.fullAmount,
            priceData.currencyCode,
          )
        : null;
      const isSoldOut = isProductSoldOut(
        pteProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );

      return {
        get cardKey() {
          return `${this.service}-${this.vendor}`;
        },
        service: 'test',
        serviceLabel: 'Tests',
        vendor: 'pte',
        badge: '',
        image: PteBanner,
        title: '',
        description:
          'Pick the world-leading Pearson Test of English (PTE), a secure computer-based test accepted worldwide.',
        shareLinkButtonName: 'Share link',
        applyButtonName: isSoldOut ? 'Sold Out' : 'Buy now',
        applyButtonCTAUrl: getApplyButtonLink(
          pteProduct,
          userId,
          agencyCountry,
          isNepalShopifyStoreEnabled,
          filters.nationality ? [filters.nationality] : [],
          countries,
        ),
        vendorUrl: getVendorUrl(pteProduct),
        disabled: !pteProduct || !!productsError || isSoldOut,
        pricing,
        isSoldOut,
        isLoadingPrice: isProductsLoading,
        priceError: !!productsError,
      };
    })(),
    (() => {
      const priceData = getBestAvailablePrices(
        toeflProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      const pricing = priceData
        ? getTestCardPricing(
            priceData.discountedAmount,
            priceData.fullAmount,
            priceData.currencyCode,
          )
        : null;
      const isSoldOut = isProductSoldOut(
        toeflProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      return {
        get cardKey() {
          return `${this.service}-${this.vendor}`;
        },
        service: 'test',
        serviceLabel: 'Tests',
        vendor: 'toefl',
        badge: '',
        image: ToeflBanner,
        title: '',
        description:
          'Choose the Test of English as a Foreign Language (TOEFL) accepted by 11,000+ institutions globally.',
        shareLinkButtonName: 'Share link',
        applyButtonName: isSoldOut ? 'Sold Out' : 'Buy now',
        applyButtonCTAUrl: getApplyButtonLink(
          toeflProduct,
          userId,
          agencyCountry,
          isNepalShopifyStoreEnabled,
          filters.nationality ? [filters.nationality] : [],
          countries,
        ),
        vendorUrl: getVendorUrl(toeflProduct),
        disabled: !toeflProduct || !!productsError || isSoldOut,
        pricing,
        isSoldOut,
        isLoadingPrice: isProductsLoading,
        priceError: !!productsError,
      };
    })(),
    (() => {
      const priceData = getBestAvailablePrices(
        duolingoProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      const pricing = priceData
        ? getTestCardPricing(
            priceData.discountedAmount,
            priceData.fullAmount,
            priceData.currencyCode,
          )
        : null;
      const isSoldOut = isProductSoldOut(
        duolingoProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      return {
        get cardKey() {
          return `${this.service}-${this.vendor}`;
        },
        service: 'test',
        serviceLabel: 'Tests',
        vendor: 'duolingo',
        badge: '',
        image: DuolingoBanner,
        title: '',
        description:
          'The convenient, fast, and affordable English test trusted by 4,500+ institutions around the world.',
        shareLinkButtonName: 'Share link',
        applyButtonName: isSoldOut ? 'Sold Out' : 'Buy now',
        applyButtonCTAUrl: getApplyButtonLink(
          duolingoProduct,
          userId,
          agencyCountry,
          isNepalShopifyStoreEnabled,
          filters.nationality ? [filters.nationality] : [],
          countries,
        ),
        vendorUrl: getVendorUrl(duolingoProduct),
        disabled: !duolingoProduct || !!productsError || isSoldOut,
        pricing,
        isSoldOut,
        isLoadingPrice: isProductsLoading,
        priceError: !!productsError,
      };
    })(),
    (() => {
      const priceData = getBestAvailablePrices(
        greProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      const pricing = priceData
        ? getTestCardPricing(
            priceData.discountedAmount,
            priceData.fullAmount,
            priceData.currencyCode,
          )
        : null;
      const isSoldOut = isProductSoldOut(
        greProduct,
        filters.nationality ? [filters.nationality] : [],
        countries,
      );
      return {
        get cardKey() {
          return `${this.service}-${this.vendor}`;
        },
        service: 'test',
        serviceLabel: 'Tests',
        vendor: 'gre',
        badge: '',
        image: GreBanner,
        title: '',
        description:
          'The ETS GRE (Graduate Record Examination) is the globally recognized, gold-standard admissions test trusted by top graduate, business, and law schools.',
        shareLinkButtonName: 'Share link',
        applyButtonName: isSoldOut ? 'Sold Out' : 'Buy now',
        applyButtonCTAUrl: getApplyButtonLink(
          greProduct,
          userId,
          agencyCountry,
          isNepalShopifyStoreEnabled,
          filters.nationality ? [filters.nationality] : [],
          countries,
        ),
        vendorUrl: getVendorUrl(greProduct),
        disabled: !greProduct || !!productsError || isSoldOut,
        pricing,
        isSoldOut,
        isLoadingPrice: isProductsLoading,
        priceError: !!productsError,
      };
    })(),
  ];

  const filteredCards = applyCardFilters(cardsData, 'test', filters);

  return {
    cardsData: filteredCards,
    isLoading: isProductsLoading || !decisionEngineReady,
    error: productsError,
  };
};
